import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { ApiError, api, type Me } from '../api'
import { Field } from '../components/bits'
import { Link } from '../router'
import { useT } from '../lang'

/* The pages you reach without being signed in: asking for a link, and spending
 * one.
 *
 * These render from `Gate` *before* the session check, which is the whole
 * point -- a person following a reset link has no session, and the ordinary
 * flow would show them the login form they cannot get past.
 *
 * **The token arrives in the fragment, never the path.** A fragment is not sent
 * to the server, so it stays out of access logs and Referer headers, and -- the
 * reason it is not merely tidy -- a corporate link scanner cannot spend it.
 * Defender and its equivalents fetch every URL in an inbound message; against a
 * single-use token in the path, that fetch is the redemption, and the recipient
 * clicks a link that has already been used. It is read once here and removed
 * from the address bar immediately, so a screenshot or a shoulder does not
 * carry it either.
 */

export type AuthKind = 'forgot' | 'reset' | 'invite' | 'magic' | 'email'

const KINDS: Record<string, AuthKind> = {
  '/auth/forgot': 'forgot',
  '/auth/reset': 'reset',
  '/auth/invite': 'invite',
  '/auth/magic': 'magic',
  '/auth/email': 'email',
}

/** Which signed-out page this path is, if any. Used by Gate before it decides
 *  whether to show the login form. */
export function authKind(path: string): AuthKind | null {
  return KINDS[path] ?? null
}

/** Read the token out of the fragment once, then take it off the address bar. */
function useHashToken(): string {
  const [token] = useState(() => window.location.hash.replace(/^#/, ''))
  useEffect(() => {
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname)
    }
  }, [])
  return token
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="login-wrap">
      <div className="login">
        <div className="wordmark">
          <span className="dot" aria-hidden="true" />
          Phoniq
        </div>
        {children}
      </div>
    </div>
  )
}

function message(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not reach the receiver.'
  switch (err.code) {
    case 'weak_password':
      // The server's detail is the actionable half ("at least 12 characters"),
      // and it is written to be shown.
      return `That password will not do: ${err.detail ?? 'choose another'}.`
    case 'invalid_token':
      return 'This link has expired or has already been used.'
    case 'too_many_attempts':
      return 'Too many attempts. Try again in a few minutes.'
    case 'email_taken':
      return 'That address already belongs to another account.'
    default:
      return err.detail ?? err.code
  }
}

export function AuthPage({
  kind, onSignedIn,
}: { kind: AuthKind; onSignedIn: (me: Me) => void }) {
  if (kind === 'forgot') return <Forgot />
  if (kind === 'magic') return <ConsumeMagic onSignedIn={onSignedIn} />
  if (kind === 'email') return <ConfirmEmail />
  return <SetPassword kind={kind} onSignedIn={onSignedIn} />
}

/* ------------------------------------------------------------------ forgot */

function Forgot() {
  const t = useT()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [magic, setMagic] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post(magic ? '/v1/auth/magic' : '/v1/auth/forgot', { email })
      setSent(true)
    } catch (err) {
      setError(message(err))
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <Frame>
        {/* Deliberately says nothing about whether the address is on file. The
            server answers identically either way -- same body, same timing --
            and a page that said "no such account" would hand back exactly what
            that costs a queued message to withhold. */}
        <div className="notice">
          {t('If that address has an account, a link is on its way. It is good for one use.')}
        </div>
        <p className="faint" style={{ textAlign: 'center', marginTop: 20, fontSize: 12 }}>
          <Link to="/">{t('Back to sign in')}</Link>
        </p>
      </Frame>
    )
  }

  return (
    <Frame>
      <form className="card stack" onSubmit={submit} style={{ gap: 18 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>
          {magic ? t('Sign in by email') : t('Reset your password')}
        </h2>
        <Field label="Email">
          <input
            type="email" value={email} autoComplete="username" required autoFocus
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        {error && <div className="notice bad">{error}</div>}

        <button className="btn primary" type="submit" disabled={busy || !email}>
          {busy ? t('Sending…') : t('Send me a link')}
        </button>

        {/* Both doors, one form. Some people would rather not keep a password
            at all and some would rather not wait for mail; offering only one is
            a preference dressed as a design. */}
        <button type="button" className="btn ghost sm"
          onClick={() => setMagic(!magic)}>
          {magic ? t('I would rather reset my password') : t('Send me a sign-in link instead')}
        </button>
      </form>
    </Frame>
  )
}

/* ------------------------------------------------- invite and reset (shared) */

function SetPassword({
  kind, onSignedIn,
}: { kind: 'reset' | 'invite'; onSignedIn: (me: Me) => void }) {
  const t = useT()
  const token = useHashToken()
  const [checked, setChecked] = useState(false)
  const [valid, setValid] = useState(false)
  const [who, setWho] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Asked before the form is shown, so "this link has expired" arrives before
  // somebody chooses a password rather than after. The check does not spend the
  // token; the submit does.
  useEffect(() => {
    if (!token) {
      setChecked(true)
      return
    }
    api
      .get<{ valid: boolean; email?: string }>(
        `/v1/auth/token?purpose=${kind}&token=${encodeURIComponent(token)}`)
      .then((r) => {
        setValid(r.valid)
        setWho(r.email ?? null)
      })
      .catch(() => setValid(false))
      .finally(() => setChecked(true))
  }, [token, kind])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post(kind === 'invite' ? '/v1/auth/accept' : '/v1/auth/reset',
        { token, password })
      // Same as Login: read identity back from the server rather than trusting
      // the response, so role and scope always come from one place.
      onSignedIn(await api.get<Me>('/v1/me'))
    } catch (err) {
      setError(message(err))
      setBusy(false)
    }
  }

  if (!checked) return <Frame><div className="notice">{t('Checking the link…')}</div></Frame>

  if (!valid) {
    return (
      <Frame>
        <div className="notice bad">
          {t('This link has expired or has already been used.')}
        </div>
        <p className="faint" style={{ textAlign: 'center', marginTop: 20, fontSize: 12 }}>
          <Link to="/auth/forgot">{t('Ask for a new one')}</Link>
        </p>
      </Frame>
    )
  }

  const mismatch = again.length > 0 && password !== again

  return (
    <Frame>
      <form className="card stack" onSubmit={submit} style={{ gap: 18 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>
          {kind === 'invite' ? t('Choose a password') : t('Set a new password')}
        </h2>
        {who && <p className="muted" style={{ margin: 0, fontSize: 14 }}>{who}</p>}

        <Field label="New password"
          hint={t('At least 12 characters. Length beats punctuation.')}>
          <input
            type="password" value={password} autoComplete="new-password"
            required autoFocus minLength={12}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="Repeat it">
          <input
            type="password" value={again} autoComplete="new-password" required
            onChange={(e) => setAgain(e.target.value)}
          />
        </Field>

        {mismatch && <div className="notice bad">{t('The two do not match.')}</div>}
        {error && <div className="notice bad">{error}</div>}

        <button className="btn primary" type="submit"
          disabled={busy || !password || password !== again}>
          {busy ? t('Saving…') : t('Save and sign in')}
        </button>

        {/* Stated because it is a consequence people should not discover on
            their phone an hour later. */}
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          {t('Every other signed-in device will be signed out.')}
        </p>
      </form>
    </Frame>
  )
}

/* ------------------------------------------------------------- magic link */

function ConsumeMagic({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const t = useT()
  const token = useHashToken()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setError('This link is incomplete.')
      return
    }
    let cancelled = false
    api
      .post('/v1/auth/magic/consume', { token })
      .then(() => api.get<Me>('/v1/me'))
      .then((me) => { if (!cancelled) onSignedIn(me) })
      .catch((err) => { if (!cancelled) setError(message(err)) })
    return () => { cancelled = true }
    // Once. A second run under StrictMode would spend a token that is good for
    // exactly one use, and the user would land on "already used".
  }, [token])

  return (
    <Frame>
      {error ? (
        <>
          <div className="notice bad">{error}</div>
          <p className="faint" style={{ textAlign: 'center', marginTop: 20, fontSize: 12 }}>
            <Link to="/auth/forgot">{t('Ask for a new one')}</Link>
          </p>
        </>
      ) : (
        <div className="notice">{t('Signing you in…')}</div>
      )}
    </Frame>
  )
}

/* --------------------------------------------------------- email confirmed */

function ConfirmEmail() {
  const t = useT()
  const token = useHashToken()
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setState('failed')
      setError('This link is incomplete.')
      return
    }
    let cancelled = false
    api
      .post('/v1/auth/email/confirm', { token })
      .then(() => { if (!cancelled) setState('done') })
      .catch((err) => {
        if (!cancelled) { setError(message(err)); setState('failed') }
      })
    return () => { cancelled = true }
  }, [token])

  return (
    <Frame>
      {state === 'working' && <div className="notice">{t('Confirming…')}</div>}
      {state === 'done' && (
        <div className="notice">
          {t('Confirmed. That is the address you sign in with now.')}
        </div>
      )}
      {state === 'failed' && <div className="notice bad">{error}</div>}
      <p className="faint" style={{ textAlign: 'center', marginTop: 20, fontSize: 12 }}>
        <Link to="/">{t('Back to sign in')}</Link>
      </p>
    </Frame>
  )
}
